/**
 * Locomotion model.
 *
 * Phase 9 direct control (the default): on the ground, friction and
 * acceleration toward the wish velocity, so starts and stops are crisp and
 * speed above the run speed bleeds off unless the player keeps hopping or
 * sliding. In the air, below airControlSpeed the player drifts toward the
 * input; above it only strafing changes the velocity (see below).
 *
 * Phase 2–8 heading model (directControl 0), "controlled regime + momentum regime":
 * Below the run speed the player has direct, exponentially-smoothed control.
 * Above it, ground input can only *steer* (rotate the velocity, speed
 * preserved) or *brake*. In the air, strafing can add speed (Phase 8): input
 * accelerates along the wish direction only while the velocity's component
 * along it is small, so speed is built by strafing and turning, not by holding
 * forward. Slides and wall-runs are in Techniques. See MOVEMENT_SPEC.md.
 */
import { Vec3, approachFactor, clamp } from '../core/Vec3';
import type { MovementConfig } from '../core/Config';
import { findBehavior, type SurfaceMaterialDef } from '../world/SurfaceMaterials';
import type { MovementIntent } from './MovementRules';

export interface Traction {
  accel: number;
  decay: number;
  turn: number;
}

const NEUTRAL_TRACTION: Readonly<Traction> = Object.freeze({ accel: 1, decay: 1, turn: 1 });

export function tractionOf(material: SurfaceMaterialDef | null, out: Traction): Traction {
  const t = material ? findBehavior(material, 'traction') : null;
  if (!t) return Object.assign(out, NEUTRAL_TRACTION);
  out.accel = t.accelScale;
  out.decay = t.momentumDecayScale;
  out.turn = t.turnScale;
  return out;
}

/** Turn rate (rad/s) at a given speed: ω(s) = base / (1 + s / ref). */
export function turnRate(base: number, speed: number, ref: number): number {
  return base / (1 + speed / ref);
}

/** Cosine below which input counts as opposing the current motion (brake). */
const BRAKE_ALIGNMENT = -0.5;

/**
 * Lead-steering turn multiplier for an input leading the velocity by `phi`
 * radians: `gain` up to `leadAngle`, then linear to `sideGain` at 90° and beyond.
 */
export function leadFactor(phi: number, gain: number, sideGain: number, leadAngle: number): number {
  if (phi <= leadAngle) return gain;
  const t = Math.min(1, (phi - leadAngle) / (Math.PI / 2 - leadAngle));
  return gain + (sideGain - gain) * t;
}

export class Locomotion {
  private readonly wish = new Vec3();
  private readonly target = new Vec3();
  private readonly vertical = new Vec3();
  private readonly slope = new Vec3();
  private readonly tmp = new Vec3();
  private readonly traction: Traction = { ...NEUTRAL_TRACTION };

  constructor(private readonly cfg: Readonly<MovementConfig>) {}

  /** Ground model. `velocity` is assumed (approximately) tangent to the ground plane. */
  ground(velocity: Vec3, intent: MovementIntent, normal: Readonly<Vec3>, up: Readonly<Vec3>, gravity: number, material: SurfaceMaterialDef | null, dt: number, crouched = false): void {
    const cfg = this.cfg;
    const tr = tractionOf(material, this.traction);
    velocity.projectOnPlane(normal);
    const hasInput = intent.wishStrength > 0;
    const wish = this.wish.set(0, 0, 0);
    if (hasInput) {
      // Input direction follows the slope so uphill/downhill input is along the surface.
      wish.copy(intent.wishDir).projectOnPlane(normal).normalize();
    }
    const top = crouched ? cfg.crouchSpeed : cfg.runSpeed;
    const run = top * (hasInput ? intent.wishStrength : 1);
    const speed = velocity.length();

    // Direct control: friction acts at or below the top speed and whenever input is released (a crisp stop);
    // with input above the top speed the momentum regime keeps the speed (steer, brake, slow decay).
    if (cfg.directControl > 0 && (speed <= top + 1e-6 || !hasInput)) {
      // Friction removes the part of the velocity not going where the player steers (all of it with no
      // input), so turns and counter-strafes are crisp; acceleration then builds speed along the input.
      const wanted = this.target.copy(wish).scale(Math.max(0, velocity.dot(wish)));
      const unwanted = this.tmp.copy(velocity).sub(wanted);
      this.friction(unwanted, unwanted.length(), cfg.groundFriction * tr.accel, dt);
      velocity.copy(wanted).add(unwanted);
      if (hasInput) {
        const add = run - velocity.dot(wish);
        if (add > 0) velocity.addScaled(wish, Math.min(add, cfg.groundAccel * tr.accel * run * dt));
        // A partial input (analog strength < 1) asks for less than the current speed: brake the excess like friction.
        else if (add < 0) velocity.addScaled(wish, -Math.min(-add, Math.max(-add, cfg.stopSpeed) * cfg.groundFriction * tr.accel * dt));
        // Mid-turn, the old and new directions can sum past the top speed; acceleration never adds speed beyond it.
        const s = velocity.length();
        if (s > top) velocity.scale(top / s);
      }
    } else if (speed <= top) {
      const target = this.target.copy(wish).scale(run);
      const rate = (hasInput ? cfg.groundAccelRate : cfg.groundDecelRate) * tr.accel;
      velocity.addScaled(target.sub(velocity), approachFactor(rate, dt));
    } else {
      this.momentumRegime(velocity, speed, wish, hasInput, top, cfg.groundTurnRate * tr.turn, cfg.brakeDecel * tr.accel, dt);
      // Excess speed decays exponentially toward the run (or crouch) speed, never below it.
      const s = velocity.length();
      const decayed = top + (s - top) * Math.exp(-cfg.momentumDecayRate * tr.decay * dt);
      if (s > 1e-9) velocity.scale(decayed / s);
    }

    this.slopeGravity(velocity, normal, up, gravity, dt);
  }

  /** Ground friction (direct control): removes max(speed, stopSpeed) × rate × dt of a velocity's speed, never reversing it. */
  private friction(velocity: Vec3, speed: number, rate: number, dt: number): void {
    if (speed < 1e-9) return;
    const next = Math.max(0, speed - Math.max(speed, this.cfg.stopSpeed) * rate * dt);
    velocity.scale(next / speed);
  }

  /** Landing grace (Phase 8): on the ground, but no friction, steering or braking yet. */
  coast(velocity: Vec3, normal: Readonly<Vec3>, up: Readonly<Vec3>, gravity: number, dt: number): void {
    velocity.projectOnPlane(normal);
    this.slopeGravity(velocity, normal, up, gravity, dt);
  }

  /**
   * Slope gravity: tangential component of gravity along the ground plane.
   * Blended in with speed so standing still on a walkable slope does not creep.
   */
  private slopeGravity(velocity: Vec3, normal: Readonly<Vec3>, up: Readonly<Vec3>, gravity: number, dt: number): void {
    const cfg = this.cfg;
    const slope = this.slope.copy(up).scale(-gravity).projectOnPlane(normal);
    const w = clamp((velocity.length() - 0.5 * cfg.runSpeed) / (0.5 * cfg.runSpeed), 0, 1);
    velocity.addScaled(slope, cfg.slopeGain * w * dt);
  }

  /** Air model: drift toward the input below the air control speed, strafing and heading steering above it; gravity applied by the caller. */
  air(velocity: Vec3, intent: MovementIntent, up: Readonly<Vec3>, dt: number): void {
    const cfg = this.cfg;
    if (intent.wishStrength <= 0) return; // no input: momentum is untouched
    const vUp = velocity.dot(up);
    const vertical = this.vertical.copy(up).scale(vUp);
    velocity.sub(vertical); // planar part
    const wish = this.wish.copy(intent.wishDir);
    const speed = velocity.length();
    if (speed < cfg.airControlSpeed) {
      const opposing = speed > 1e-9 && velocity.dot(wish) / speed < BRAKE_ALIGNMENT;
      const target = this.target.copy(wish).scale(cfg.airControlSpeed * intent.wishStrength);
      velocity.addScaled(target.sub(velocity), approachFactor(cfg.airAccelRate, dt));
      const after = velocity.length();
      if (cfg.airSteerKeepsSpeed > 0 && !opposing && after < speed && after > 1e-9) velocity.scale(speed / after);
    } else {
      const lead = cfg.airLeadGain !== 1 || cfg.airLeadSideGain !== 1;
      const phi = lead ? Math.acos(clamp(velocity.dot(wish) / speed, -1, 1)) : 0;
      const k = lead ? leadFactor(phi, cfg.airLeadGain, cfg.airLeadSideGain, (cfg.airLeadAngleDeg * Math.PI) / 180) : 1;
      this.momentumRegime(velocity, speed, wish, true, cfg.airControlSpeed, cfg.airTurnRate * k, cfg.airBrakeDecel, dt);
    }
    this.strafe(velocity, wish, intent.wishStrength, dt);
    velocity.add(vertical);
  }

  /** Air strafing on the planar velocity: accelerate along `wish` while the velocity's component along it is below the strafe speed. */
  private strafe(planar: Vec3, wish: Readonly<Vec3>, strength: number, dt: number): void {
    const cfg = this.cfg;
    const add = cfg.airStrafeSpeed * strength - planar.dot(wish);
    if (add <= 0) return;
    planar.addScaled(wish, Math.min(add, cfg.airStrafeAccel * strength * dt));
  }

  /** Steering/braking above a regime speed. Never increases speed. */
  private momentumRegime(velocity: Vec3, speed: number, wish: Readonly<Vec3>, hasInput: boolean, floorSpeed: number, baseTurn: number, brake: number, dt: number): void {
    if (!hasInput || wish.lengthSq() === 0) return;
    const align = velocity.dot(wish) / speed;
    if (align < BRAKE_ALIGNMENT) {
      const next = Math.max(floorSpeed, speed - brake * dt);
      velocity.scale(next / speed);
      return;
    }
    velocity.rotateToward(wish, turnRate(baseTurn, speed, this.cfg.turnSpeedRef) * dt, this.tmp);
  }
}
